import type { CommentConfig } from 'component/comment';
import { boostComments, commentTable } from 'component/comment';
import { DropDownSelector } from 'component/popup';
import { Heading } from 'component/text';
import type { ModeratedComment } from 'feature/moderation/ai-premoderator';
import { View } from 'react-native';
import type { Datastore } from 'util/datastore';
import { useCollection, useDatastore, useSessionData } from 'util/datastore';
import { downvoteTable, upvoteTable } from 'util/stdconfig';
import type { Feature } from 'util/stdtypes';
import { t } from 'util/stdtypes';

const sortValues = {
    recent: 'recent',
    oldest: 'oldest',
    popular: 'popular',
};

export const CommentsSortFeature: Feature<CommentConfig, {}> = {
    name: 'Comment Sorting Feature',
    key: 'commentssort',
    dev: true,
    providesConfig: {
        commentRankers: [{ key: 'boost-by-upvote', label: t('Boost by upVote'), ranker: rankSelectedComment }],
        pageTopLeftWidgets: [CommentSorterTitle],
        pageTopRightWidgets: [CommentSorter],
    },
};

export function rankSelectedComment(datastore: Datastore, comments: ModeratedComment[]) {
    const { commentBooster } = datastore.getConfig<CommentConfig>();
    const dropDownValue = datastore.getSessionData(['commentSortValue', 'value']);
    const upVotes = datastore.getCollection(upvoteTable);
    const downVotes = datastore.getCollection(downvoteTable);
    let topComments, otherComments, rankedComments, boostedComments;

    if (sortValues.recent === dropDownValue) {
        return [...comments.sort((x, y) => x.time - y.time).reverse()];
    } else if (sortValues.oldest === dropDownValue) {
        return [...comments.sort((x, y) => x.time - y.time)];
    } else {
        topComments = comments.filter((comment) => upVotes.some((upVote) => upVote.comment === comment.key));
        const sortedRankedComments = topComments.sort((a, b) => {
            const aUpVotes = upVotes.filter((upVote) => upVote.comment === a.key).length;
            const aDownVotes = downVotes.filter((downVote) => downVote.comment === a.key).length;
            const bUpVotes = upVotes.filter((upVote) => upVote.comment === b.key).length;
            const bDownVotes = downVotes.filter((downVote) => downVote.comment === b.key).length;
            const aScore = aUpVotes - aDownVotes;
            const bScore = bUpVotes - bDownVotes;
            return bScore - aScore;
        });
        otherComments = comments.filter((comment) => !upVotes.some((upVote) => upVote.comment === comment.key));
        rankedComments = sortedRankedComments;
        boostedComments = commentBooster ? boostComments({ comments, rankedComments, datastore, commentBooster }) : rankedComments;
        return [...boostedComments, ...otherComments];
    }
}

export function CommentSorterTitle() {
    return <Heading label={t('All questions')} />;
}

export function CommentSorter() {
    const dropDownValue = useSessionData(['commentSortValue', 'value']);
    const datastore = useDatastore();
    const comments = useCollection(commentTable);

    if (comments.length == 0) {
        return null;
    }

    function handleDropDown(value) {
        datastore.setSessionData(['commentSortValue', 'value'], value);
    }

    return (
        <View style={{ alignSelf: 'flex-end' }}>
            <DropDownSelector
                label={t('Sort by')}
                onChange={handleDropDown}
                options={[
                    { key: sortValues.recent, label: t('Most recent') },
                    { key: sortValues.oldest, label: t('Oldest') },
                    { key: sortValues.popular, label: t('Most popular') },
                ]}
                value={dropDownValue ?? sortValues.popular}
            />
        </View>
    );
}
